import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Phone, Mail, Plus } from 'lucide-react';
import { TreatmentCard } from '../components/TreatmentCard';
import { Button } from '../components/ui/button';
import type { Client, Treatment } from '../types';
import { useAuth } from '../hooks/useAuth';

interface RowCatalyst {
  Clientes: {
    ROWID: string;
    Nombres: string;
    Apellidos: string;
    Telefono?: string;
    Tel?: string;
    Email?: string;
    email?: string;
  };
}

interface TreatmentRowCatalyst {
  Tratamientos: {
    ROWID: string;
    Specialist: string;
    Categoria: Treatment['category'];
    Estado?: string;
    FotosAntes?: string | null;
    FotoDespues?: string | null;
    FormData?: string;
    ID_Cliente: string;
    Fecha: string;
    IsDraft?: boolean;
    Status?: string;
  };
}

export function ClientProfile() {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const { user } = useAuth();
  const [client, setClient] = useState<Client | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clientTreatments, setClientTreatments] = useState<Treatment[]>([]);
  const shouldHideContactInfo = user?.role === 'app_user';

  useEffect(() => {
    if (!clientId) {
      setError('Falta el id del cliente');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    Promise.all([
      fetch(`/server/fichas_cliente_sisters_peluqueros_function/client?id=${encodeURIComponent(clientId)}`)
        .then(async response => {
          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.error || 'No se pudo cargar el cliente');
          }

          return data as RowCatalyst;
        }),
      fetch(`/server/fichas_cliente_sisters_peluqueros_function/client/${encodeURIComponent(clientId)}/treatments`)
        .then(async response => {
          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.error || 'No se pudo cargar el historial de tratamientos');
          }

          return data as TreatmentRowCatalyst[];
        })
    ])
      .then(([clientData, treatmentsData]) => {
        const rawClient = clientData.Clientes;

        setClient({
          id: rawClient.ROWID,
          firstName: rawClient.Nombres,
          lastName: rawClient.Apellidos,
          phone: rawClient.Telefono || rawClient.Tel || '',
          email: rawClient.Email || rawClient.email || ''
        });

        const mappedTreatments = treatmentsData.map(({ Tratamientos: rawTreatment }) => {
          let parsedFormData: Record<string, any> = {};

          try {
            parsedFormData = rawTreatment.FormData ? JSON.parse(rawTreatment.FormData) : {};
          } catch (parseError) {
            console.error('Error al parsear FormData del tratamiento:', parseError);
          }

          const photos = [
            rawTreatment.FotosAntes
              ? { id: `${rawTreatment.ROWID}-antes`, url: rawTreatment.FotosAntes, type: 'antes' as const }
              : null,
            rawTreatment.FotoDespues
              ? { id: `${rawTreatment.ROWID}-despues`, url: rawTreatment.FotoDespues, type: 'despues' as const }
              : null,
          ].filter(Boolean) as Treatment['photos'];

          return {
            id: rawTreatment.ROWID,
            clientId: rawTreatment.ID_Cliente,
            category: rawTreatment.Categoria,
            date: rawTreatment.Fecha,
            specialist: rawTreatment.Specialist,
            photos,
            formData: parsedFormData,
            isDraft: Boolean(rawTreatment.IsDraft),
          };
        });

        setClientTreatments(mappedTreatments);
      })
      .catch(fetchError => {
        console.error('Error al obtener el cliente:', fetchError);
        setError(fetchError instanceof Error ? fetchError.message : 'Error al obtener el cliente');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [clientId]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="text-gray-600 hover:text-[#E91E8C]"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl text-[#2d2d2d]">Ficha de Cliente</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        {isLoading ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
            <p className="text-gray-500">Cargando cliente...</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
            <p className="text-red-500">{error}</p>
          </div>
        ) : !client ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
            <p className="text-gray-500">Cliente no encontrado</p>
          </div>
        ) : (
          <>
        {/* Datos del Cliente */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <h2 className="text-2xl mb-4 text-[#2d2d2d]">
            {client.firstName} {client.lastName}
          </h2>
          
          {!shouldHideContactInfo && (
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-gray-700">
                <Phone className="w-5 h-5 text-[#E91E8C]" />
                <span>{client.phone}</span>
              </div>
              <div className="flex items-center gap-3 text-gray-700">
                <Mail className="w-5 h-5 text-[#E91E8C]" />
                <span>{client.email}</span>
              </div>
            </div>
          )}
        </div>

        {/* Botón Añadir Tratamiento */}
        <Button
          onClick={() => navigate(`/client/${clientId}/new-treatment`)}
          className="w-full h-14 bg-[#E91E8C] hover:bg-[#d01a7d] text-white mb-6"
        >
          <Plus className="w-5 h-5 mr-2" />
          Añadir Nuevo Tratamiento
        </Button>

        {/* Historial de Tratamientos */}
        <div className="mb-4">
          <h3 className="mb-4 text-[#2d2d2d]">Historial de Tratamientos</h3>
        </div>

        <div className="space-y-4">
          {clientTreatments.length > 0 ? (
            clientTreatments.map(treatment => (
              <TreatmentCard
                key={treatment.id}
                treatment={treatment}
                onClick={() => navigate(
                  treatment.isDraft
                    ? `/client/${clientId}/treatment/${treatment.id}/edit`
                    : `/treatment/${treatment.id}`
                )}
              />
            ))
          ) : (
            <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-100">
              <p className="text-gray-500">No hay tratamientos registrados</p>
              <p className="text-sm text-gray-400 mt-2">
                Añade el primer tratamiento para esta clienta
              </p>
            </div>
          )}
        </div>
          </>
        )}
      </main>
    </div>
  );
}
